import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { apiGet, apiDelete, apiPost } from '../utils/api';
import { toast } from 'sonner';

function History() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await apiGet('/history');
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (monthId) => {
    if (!window.confirm('Are you sure you want to delete this saved month? This cannot be undone.')) {
      return;
    }

    setDeleting(monthId);
    try {
      const response = await apiDelete(`/history/${monthId}`);
      if (response.ok) {
        setHistory(history.filter(item => item.id !== monthId));
        toast.success('Month deleted successfully');
      } else {
        toast.error('Failed to delete month');
      }
    } catch (error) {
      console.error('Error deleting month:', error);
      toast.error('Error deleting month');
    } finally {
      setDeleting(null);
    }
  };

  const handleExport = async (monthId, monthName) => {
    setExporting(monthId);
    try {
      const response = await apiGet(`/history/${monthId}/export`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${monthName}_export.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Export downloaded!');
      } else {
        toast.error('Failed to export');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error exporting month');
    } finally {
      setExporting(null);
    }
  };

  const handleRestore = async (monthId, monthName) => {
    if (!window.confirm(
      `⚠️ Restore "${monthName}"?\n\n` +
      'This will:\n' +
      '• Replace your current income & expenses with saved data\n' +
      '• Restore bill paid statuses from that month\n\n' +
      'Your current data will be overwritten!'
    )) {
      return;
    }

    setRestoring(monthId);
    try {
      const response = await apiPost(`/history/${monthId}/restore`, {});
      if (response.ok) {
        const data = await response.json();
        toast.success(`Restored from ${data.restored_from}!`);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to restore');
      }
    } catch (error) {
      console.error('Error restoring:', error);
      toast.error('Error restoring month');
    } finally {
      setRestoring(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading history...</p>
        </div>
      </div>
    );
  }

  // Separate personal and household budgets
  const personalBudgets = history.filter(item => item.type !== 'household');
  const householdBudgets = history.filter(item => item.type === 'household');

  return (
    <main className="space-y-6" data-testid="history-page" role="main" aria-label="Budget History">
      {/* Header */}
      <header>
        <h1 className="text-3xl font-bold text-gray-900" data-testid="history-title">Budget History</h1>
        <p className="text-gray-600 mt-1">View, export, or restore your saved budget months</p>
      </header>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <span className="text-blue-600 text-xl">💡</span>
          <div>
            <h3 className="font-medium text-blue-900">How to Use Budget History</h3>
            <ul className="text-sm text-blue-800 mt-1 space-y-1">
              <li>• <strong>Export to CSV</strong> - Download a spreadsheet of your budget data</li>
              <li>• <strong>Restore</strong> - Undo a wrap-up by restoring a saved snapshot</li>
              <li>• <strong>Delete</strong> - Permanently remove a saved month</li>
            </ul>
          </div>
        </div>
      </div>

      {history.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200">
          <div className="text-6xl mb-4">📅</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No History Yet</h3>
          <p className="text-gray-600 mb-4">When you "Wrap Up & Start Fresh" on the Income page, your budget snapshot will appear here.</p>
          <p className="text-sm text-gray-500">Tip: History helps you track your finances month over month!</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Household Budgets Section */}
          {householdBudgets.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>🏠</span> Household Budgets
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {householdBudgets.map((item) => (
                  <HouseholdHistoryCard
                    key={item.id}
                    item={item}
                    onExport={handleExport}
                    onRestore={handleRestore}
                    onDelete={handleDelete}
                    exporting={exporting}
                    restoring={restoring}
                    deleting={deleting}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Personal Budgets Section */}
          {personalBudgets.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>👤</span> Personal Budgets
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {personalBudgets.map((item) => (
                  <PersonalHistoryCard
                    key={item.id}
                    item={item}
                    onExport={handleExport}
                    onRestore={handleRestore}
                    onDelete={handleDelete}
                    exporting={exporting}
                    restoring={restoring}
                    deleting={deleting}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );

// ========== Personal Budget History Card ==========
}
function PersonalHistoryCard({ item, onExport, onRestore, onDelete, exporting, restoring, deleting }) {
  const summary = item.income_settings?.summary || {};
  
  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`history-item-${item.id}`}
    >
      {/* Header - Gray for Personal */}
      <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-5 py-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg">👤</span>
              <h3 className="text-lg font-bold">{item.month_name}</h3>
            </div>
            <p className="text-gray-300 text-sm">
              {format(new Date(item.created_at), 'MMM dd, yyyy')}
            </p>
          </div>
          <span className="bg-gray-600 text-gray-200 text-xs px-2 py-1 rounded-full">
            Personal
          </span>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-5 space-y-3">
        {summary.total_income !== undefined ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-green-600 font-medium">Income</p>
                <p className="text-lg font-bold text-green-700">${summary.total_income?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-xs text-red-600 font-medium">Bills</p>
                <p className="text-lg font-bold text-red-700">${summary.total_bills?.toLocaleString() || 0}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <p className="text-xs text-orange-600 font-medium">Expenses</p>
                <p className="text-lg font-bold text-orange-700">${summary.total_expenses?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-600 font-medium">Saved</p>
                <p className="text-lg font-bold text-blue-700">${summary.total_saved?.toLocaleString() || 0}</p>
              </div>
            </div>
            {summary.streak_days > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
                <p className="text-sm text-amber-800">
                  🔥 {summary.streak_days} day streak • {summary.streak_title}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500 text-sm">Legacy budget data</p>
            <p className="text-xs text-gray-400">Detailed stats not available</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="border-t border-gray-200 p-3 bg-gray-50 flex gap-2">
        <button
          onClick={() => onExport(item.id, item.month_name)}
          disabled={exporting === item.id}
          className="flex-1 py-2 px-3 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
          data-testid={`export-history-${item.id}`}
        >
          {exporting === item.id ? '⏳' : '📊'} Export
        </button>
        <button
          onClick={() => onRestore(item.id, item.month_name)}
          disabled={restoring === item.id}
          className="flex-1 py-2 px-3 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
          data-testid={`restore-history-${item.id}`}
        >
          {restoring === item.id ? '⏳' : '↩️'} Restore
        </button>
        <button
          onClick={() => onDelete(item.id)}
          disabled={deleting === item.id}
          className="py-2 px-3 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
          data-testid={`delete-history-${item.id}`}
          title="Delete this month"
        >
          {deleting === item.id ? '⏳' : '🗑️'}
        </button>
      </div>
    </div>
  );
}

// ========== Household Budget History Card ==========
function HouseholdHistoryCard({ item, onExport, onRestore, onDelete, exporting, restoring, deleting }) {
  const summary = item.income_settings?.summary || {};
  const contributors = item.contributors || [];
  const householdName = item.household_name || 'Household';
  
  return (
    <div
      className="bg-white rounded-xl shadow-sm border-2 border-amber-300 overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`history-item-${item.id}`}
    >
      {/* Header - Gold/Amber for Household */}
      <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-5 py-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg">🏠</span>
              <h3 className="text-lg font-bold">{item.month_name}</h3>
            </div>
            <p className="text-amber-100 text-sm">
              {householdName} • {format(new Date(item.created_at), 'MMM dd, yyyy')}
            </p>
          </div>
          <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full font-medium">
            Shared
          </span>
        </div>
      </div>

      {/* Contributors Roll Call */}
      {contributors.length > 0 && (
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-200">
          <p className="text-xs text-amber-700 font-medium">
            👥 Contributors: {contributors.join(', ')}
          </p>
        </div>
      )}

      {/* Summary Stats */}
      <div className="p-5 space-y-3">
        {summary.total_contributions !== undefined ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-xs text-emerald-600 font-medium">Total Contributions</p>
                <p className="text-lg font-bold text-emerald-700">${summary.total_contributions?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-xs text-red-600 font-medium">Bills</p>
                <p className="text-lg font-bold text-red-700">${summary.total_bills?.toLocaleString() || 0}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <p className="text-xs text-orange-600 font-medium">Expenses</p>
                <p className="text-lg font-bold text-orange-700">${summary.total_expenses?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-600 font-medium">Leftover</p>
                <p className="text-lg font-bold text-blue-700">${summary.leftover?.toLocaleString() || 0}</p>
              </div>
            </div>
            {summary.contributor_count > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-center">
                <p className="text-sm text-purple-800">
                  {summary.contributor_count} contributor{summary.contributor_count !== 1 ? 's' : ''} this month
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500 text-sm">Household budget data</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="border-t border-amber-200 p-3 bg-amber-50 flex gap-2">
        <button
          onClick={() => onExport(item.id, item.month_name)}
          disabled={exporting === item.id}
          className="flex-1 py-2 px-3 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
          data-testid={`export-history-${item.id}`}
        >
          {exporting === item.id ? '⏳' : '📊'} Export
        </button>
        <button
          onClick={() => onDelete(item.id)}
          disabled={deleting === item.id}
          className="py-2 px-3 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
          data-testid={`delete-history-${item.id}`}
          title="Delete this month"
        >
          {deleting === item.id ? '⏳' : '🗑️'}
        </button>
      </div>
    </div>
  );
}

export default History;
